// The owner's 2 dialogs on the Price map: mapping a Stripe price that isn't on the map yet, and
// editing a row (its label, where it's sold from, and for a place price its balance date and the
// balance its 1 pay button pays, C10). Owner only, as row level security has it (price_map's insert
// and update policies); the database audits every change as price_map_changed.
//
// Direct writes under row level security with the owner's own login (docs/data-model.md 10.4.2,
// "Map a price, unmap it"). No amount is written: amounts stay in Stripe.
import { useEffect, useId, useMemo, useState } from 'react'
import {
  Dialog, Button, Field, TextInput, Options, Select, DateField, TimeField, Checkbox, InfoNote,
  useAdmin,
} from '../ui/index.js'
import { DUE_PASSED, labelFromStripeName, priceLine, dueColumns, dueFromRow } from './model.js'

export const PRICE_COLUMNS = 'stripe_price_id, label, sold_from, status, secures_place, access_months, payments_total, opens_on_payment, default_due_days, default_due_at, balance_payment_url, outside_product_id, outside_status, updated_at'
const LINK_COLUMNS = 'stripe_price_id, product_id, cohort_id'
const BALANCE_COLUMNS = 'place_price_id, balance_price_id, pay_button'

// A labelled set of option buttons (the group is named by its visible label)
function OptionField({ label, hint, children }) {
  const id = useId()
  return (
    <div className="ad-field">
      <span className="ad-label" id={id}>{label}</span>
      {children(id)}
      {hint && <p className="ad-hint">{hint}</p>}
    </div>
  )
}

const DUE_OPTIONS = [
  { value: 'date', label: 'On a date' },
  { value: 'days', label: 'Days after it\'s secured' },
  { value: 'none', label: 'Not set yet' },
]

function DueFields({ due, setDue, nowMs }) {
  const cols = dueColumns(due)
  const passed = due.mode === 'date' && cols.default_due_at && Date.parse(cols.default_due_at) < nowMs
  return (
    <div className="ad-form pm-due">
      <OptionField label="Balance due" hint="Changes the date for places secured from now on. Anyone already holding a place keeps their own date, which you move on their page.">
        {id => <Options labelledBy={id} row value={due.mode} onChange={mode => setDue(d => ({ ...d, mode }))} options={DUE_OPTIONS} />}
      </OptionField>
      {due.mode === 'date' && (
        <div className="ad-row">
          <DateField label="Date" value={due.day} onChange={day => setDue(d => ({ ...d, day }))} />
          <TimeField label="Time, UK" value={due.at} onChange={at => setDue(d => ({ ...d, at: at || '19:00' }))} />
        </div>
      )}
      {due.mode === 'days' && (
        <Field label="Days after the place is secured">
          {props => <TextInput {...props} inputMode="numeric" value={due.days} onChange={days => setDue(d => ({ ...d, days }))} />}
        </Field>
      )}
      {passed && <InfoNote>{DUE_PASSED} Anyone who secures a place shows on Today the next morning.</InfoNote>}
    </div>
  )
}

const isLink = s => /^https:\/\/[^\s]+$/.test(String(s || '').trim())

// ---- Editing a row (owner) ---------------------------------------------------------------------

export function EditDialog({ row, amounts, onClose, onSaved }) {
  const { api, personId } = useAdmin()
  const p = row.row
  const place = row.kind === 'place' && row.status === 'mapped' ? row.place : null
  const [label, setLabel] = useState(p.label || '')
  const [from, setFrom] = useState(p.sold_from || '')
  const [due, setDue] = useState(() => dueFromRow(p))
  const [button, setButton] = useState(place?.buttonId || '')
  const buttonPrice = place?.balances.find(b => b.balance_price_id === button)?.price || null
  const [link, setLink] = useState(place ? (place.buttonLink || '') : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const nowMs = Date.now()

  // the pay button's link lives on the balance's own row: show the one already on the balance picked
  function pickButton(id) {
    setButton(id)
    const pr = place?.balances.find(b => b.balance_price_id === id)?.price
    setLink(pr?.balance_payment_url || '')
  }

  async function save() {
    setError(null)
    const name = label.trim()
    if (!name) return setError('Give it a label: a name, never an amount.')
    if (/[£$€]\s?\d/.test(name)) return setError('A label is a name, never an amount. Stripe holds the amount.')
    const patch = { label: name, sold_from: from.trim() || null, updated_by: personId }
    if (place) {
      const cols = dueColumns(due)
      if (cols.error) return setError(cols.error)
      Object.assign(patch, cols)
      if (link.trim() && !isLink(link)) return setError('A payment link starts with https://, for example https://buy.stripe.com/...')
    }
    setBusy(true)
    try {
      const saved = await api.run(api.from('price_map').update(patch).eq('stripe_price_id', p.stripe_price_id).select(PRICE_COLUMNS))
      if (!saved?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
      if (place && button && button !== place.buttonId) {
        // 1 pay button per place (price_map_balances_one_button): the old one goes first
        if (place.buttonId) {
          await api.run(api.from('price_map_balances').update({ pay_button: false })
            .eq('place_price_id', p.stripe_price_id).eq('balance_price_id', place.buttonId).select(BALANCE_COLUMNS))
        }
        const set = await api.run(api.from('price_map_balances').update({ pay_button: true })
          .eq('place_price_id', p.stripe_price_id).eq('balance_price_id', button).select(BALANCE_COLUMNS))
        if (!set?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
      }
      if (place && button && (link.trim() || null) !== (buttonPrice?.balance_payment_url || null)) {
        await api.run(api.from('price_map').update({ balance_payment_url: link.trim() || null, updated_by: personId })
          .eq('stripe_price_id', button).select(PRICE_COLUMNS))
      }
      onSaved(`Saved. ${name} is up to date.`)
    } catch (e) {
      setError(api.words(e))
      setBusy(false)
    }
  }

  const amount = row.price && row.priceKnown ? row.price : p.label
  return (
    <Dialog title="Edit this price" onClose={onClose} busy={busy} wide={Boolean(place)}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button kind="ink" busy={busy} busyLabel="Saving" onClick={save}>Save changes</Button>
      </>}>
      <p className="ad-lede pm-dialog-lede">{amount}. Stripe holds the amount, so it's changed in Stripe, never here.</p>
      <div className="ad-form">
        <Field label="Label" hint="A name, never an amount. It shows under the price on this map.">
          {props => <TextInput {...props} value={label} onChange={setLabel} maxLength={120} />}
        </Field>
        <Field label="Sold from" hint="Where people buy it, for example the masterclass sales page.">
          {props => <TextInput {...props} value={from} onChange={setFrom} maxLength={200} />}
        </Field>
        {place && <DueFields due={due} setDue={setDue} nowMs={nowMs} />}
        {place && place.balances.length > 0 && (
          <OptionField label="The pay button pays" hint="Members with a place secured see 1 button, which pays this balance.">
            {id => (
              <Options labelledBy={id} value={button} onChange={pickButton}
                options={place.balances.map(b => ({
                  value: b.balance_price_id,
                  label: amounts?.prices?.[b.balance_price_id]?.display ? `${priceLine(amounts.prices[b.balance_price_id].display, { kind: 'balance' })}` : (b.price?.label || b.balance_price_id),
                  note: b.price?.label || null,
                }))} />
            )}
          </OptionField>
        )}
        {place && button && (
          <Field label="The payment link the pay button opens" hint="The Stripe payment link that sells this balance.">
            {props => <TextInput {...props} type="url" value={link} onChange={setLink} placeholder="https://buy.stripe.com/..." />}
          </Field>
        )}
        {error && <p className="ad-error" role="alert">{error}</p>}
      </div>
    </Dialog>
  )
}

// ---- Mapping a price not on the map (owner) ----------------------------------------------------

const KINDS = [
  { value: 'full', label: 'Opens a product', note: 'Everything, from payment' },
  { value: 'place', label: 'Secures a place', note: 'Nothing opens until the balance is paid' },
  { value: 'balance', label: 'Pays a place\'s balance', note: 'Opens what the place holds' },
]

// price: an item of stripe-prices' unmapped list { id, product_id, product_name, display, interval, active }
export function MapDialog({ price, catalogue, prices, onClose, onMapped }) {
  const { api, personId } = useAdmin()
  const recurring = Boolean(price.interval)
  const places = useMemo(() => (prices || []).filter(p => p.secures_place && p.status === 'mapped'), [prices])
  const products = useMemo(() => (catalogue || [])
    .filter(c => c.access_kind === 'paid' && c.type !== 'client_space')
    .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99)), [catalogue])
  const [kind, setKind] = useState(recurring ? 'full' : '')
  const [product, setProduct] = useState('')
  const [place, setPlace] = useState('')
  const [button, setButton] = useState(false)
  const [link, setLink] = useState('')
  const [label, setLabel] = useState(() => labelFromStripeName(price.product_name) || '')
  const [from, setFrom] = useState('')
  const [due, setDue] = useState({ mode: 'none', day: '', at: '19:00', days: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // a choice made clears the line asking for it
  useEffect(() => { setError(null) }, [kind, product, place])

  const kinds = KINDS.map(k => ({
    ...k,
    disabled: (recurring && k.value !== 'full') || (k.value === 'balance' && places.length === 0),
  }))

  async function save() {
    setError(null)
    const name = label.trim()
    if (!kind) return setError('Pick what a payment on it does.')
    if ((kind === 'full' || kind === 'place') && !product) return setError('Pick the product it opens.')
    if (kind === 'balance' && !place) return setError('Pick the place this balance pays for.')
    if (!name) return setError('Give it a label: a name, never an amount.')
    if (/[£$€]\s?\d/.test(name)) return setError('A label is a name, never an amount. Stripe holds the amount.')
    if (button && link.trim() && !isLink(link)) return setError('A payment link starts with https://, for example https://buy.stripe.com/...')
    if (!price.product_id) return setError("Stripe didn't say which product this price belongs to, so it can't be mapped here.")
    const row = {
      stripe_price_id: price.id, stripe_product_id: price.product_id, label: name, sold_from: from.trim() || null,
      status: 'mapped', secures_place: kind === 'place', updated_by: personId,
    }
    if (kind === 'place') {
      const cols = dueColumns(due)
      if (cols.error) return setError(cols.error)
      Object.assign(row, cols)
    }
    if (kind === 'balance' && button && link.trim()) row.balance_payment_url = link.trim()
    setBusy(true)
    let mapped = false
    try {
      // upsert, so a second try after a failure part way finishes the job
      await api.run(api.from('price_map').upsert(row, { onConflict: 'stripe_price_id' }).select(PRICE_COLUMNS))
      mapped = true
      if (kind === 'full' || kind === 'place') {
        await api.run(api.from('price_map_products').insert({ stripe_price_id: price.id, product_id: product }).select(LINK_COLUMNS))
      } else {
        if (button) {
          await api.run(api.from('price_map_balances').update({ pay_button: false }).eq('place_price_id', place).eq('pay_button', true).select(BALANCE_COLUMNS))
        }
        await api.run(api.from('price_map_balances').insert({ place_price_id: place, balance_price_id: price.id, pay_button: button }).select(BALANCE_COLUMNS))
      }
      const opens = kind === 'balance'
        ? (prices.find(p => p.stripe_price_id === place)?.label || 'its place')
        : (products.find(c => c.id === product)?.name || 'the product')
      onMapped({ id: price.id, kind, opens, productId: kind === 'balance' ? null : product })
    } catch (e) {
      setError(mapped
        ? `The price is on the map, but what it opens wasn't saved: ${api.words(e)} Try again to finish it.`
        : api.words(e))
      setBusy(false)
    }
  }

  const amount = price.display ? priceLine(price.display, { interval: price.interval }) : 'This price'
  return (
    <Dialog title="Map this price" onClose={onClose} busy={busy} wide
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button kind="ink" busy={busy} busyLabel="Mapping" onClick={save}>Map it</Button>
      </>}>
      <p className="ad-lede pm-dialog-lede">
        {amount}{price.product_name ? `, "${labelFromStripeName(price.product_name)}" in Stripe` : ''}. Once it's on the map, a payment on it opens what you pick here.
      </p>
      <div className="ad-form">
        <OptionField label="What a payment on it does" hint={recurring ? 'It\'s a subscription, so it opens a product while it\'s paid.' : undefined}>
          {id => <Options labelledBy={id} value={kind} onChange={setKind} options={kinds} />}
        </OptionField>
        {(kind === 'full' || kind === 'place') && (
          <OptionField label={kind === 'place' ? 'The product the place is for' : 'The product it opens'}>
            {id => (
              <Options labelledBy={id} value={product} onChange={setProduct}
                options={products.map(c => ({
                  value: c.id, label: c.name, product: c,
                  disabled: !c.moved_in,
                  note: !c.moved_in ? 'On its own site until it moves' : c.state === 'draft' ? 'Draft' : null,
                }))} />
            )}
          </OptionField>
        )}
        {kind === 'balance' && (
          <>
            <Field label="The place it pays for">
              {props => (
                <Select {...props} value={place} onChange={setPlace} placeholder="Pick a place price"
                  options={places.map(pl => ({ value: pl.stripe_price_id, label: pl.label }))} />
              )}
            </Field>
            <Checkbox checked={button} onChange={setButton}>The place's 1 pay button pays this balance</Checkbox>
            {button && (
              <Field label="The payment link the pay button opens" hint="The Stripe payment link that sells this balance.">
                {props => <TextInput {...props} type="url" value={link} onChange={setLink} placeholder="https://buy.stripe.com/..." />}
              </Field>
            )}
          </>
        )}
        {kind === 'place' && <DueFields due={due} setDue={setDue} nowMs={Date.now()} />}
        <Field label="Label" hint="A name, never an amount. It shows under the price on this map.">
          {props => <TextInput {...props} value={label} onChange={setLabel} maxLength={120} />}
        </Field>
        <Field label="Sold from" hint="Where people buy it, for example the masterclass sales page.">
          {props => <TextInput {...props} value={from} onChange={setFrom} maxLength={200} />}
        </Field>
        {error && <p className="ad-error" role="alert">{error}</p>}
      </div>
    </Dialog>
  )
}

